/**
 * plant-helpers.ts — общие typed-хелперы растительного пула: Рост,
 * Фотосинтез, Крона, Созревание и Колючки.
 *
 * Все изменения Роста в пуле проходят через addGrowth/removeGrowth:
 * это единственная точка, где применяется перенаправление Подсолнуха.
 * Хранилище Роста — unit-scoped persistent Effect `plant_growth` с публичным
 * счётчиком `growth` (generic effect counter API + EffectCounterChanged).
 */
import { cellId as makeCellId, LANE_INDEXES, parseCellId, } from '@cards/engine';
import { cardRef, afterDamageDealt, afterEvent, atRoundEnd, beforeDamage, onDeath, } from '../authoring/index.js';
export const PLANT_GROWTH_ID = 'plant_growth';
export const PLANT_THORNS_ID = 'plant_thorns';
export const GROWTH_KEY = 'growth';
export const THORNS_VALUE_KEY = 'value';
export const THORNS_GROWTH_REWARDS_KEY = 'growthRewards';
export const SAPLING_DEFINITION_IDS = Object.freeze([
    'banyan_shoot',
    'blackthorn_sapling',
    'great_oak_sapling',
]);
/** Стабильные persisted-ключи (переименование = migration review). */
export const CROWN_USED_ROUND_KEY = 'crown_used_round';
export const CROWN_PENDING_LEAF_KEY = 'crown_pending_leaf';
export const CROWN_ATTACK_GROUP_KEY = 'crown_attack_group';
export function tableOf(cellId) {
    return parseCellId(cellId).tableId;
}
export function isLiveOnBoard(unit) {
    return unit !== null && unit !== undefined && unit.zone === 'board' && unit.cellId !== undefined;
}
export function hasTrait(ctx, instanceId, trait) {
    return ctx.q.cardTraits(instanceId).includes(trait);
}
/** Живые растения под контролем игрока в канонич. порядке; фильтры опциональны. */
export function alliedPlants(ctx, controllerId, options = {}) {
    const trait = options.trait ?? 'PLANT';
    return ctx.q.unitsOf(controllerId).filter((unit) => isLiveOnBoard(unit)
        && !(options.exclude?.has(unit.id) ?? false)
        && (options.tableId === undefined || tableOf(unit.cellId) === options.tableId)
        && hasTrait(ctx, unit.id, trait));
}
function counterValue(raw) {
    return typeof raw === 'number' && Number.isSafeInteger(raw) && raw >= 0 ? raw : 0;
}
export function growthEffectOf(ctx, instanceId) {
    return ctx.q.effectsOn({ kind: 'unit', instanceId })
        .find((effect) => effect.defId === PLANT_GROWTH_ID);
}
export function growthOf(ctx, instanceId) {
    return counterValue(growthEffectOf(ctx, instanceId)?.vars[GROWTH_KEY]);
}
function grantGrowthDirect(ctx, instanceId, amount, reason) {
    const existing = growthEffectOf(ctx, instanceId);
    const effectId = existing?.id ?? ctx.ops.addEffect({
        defId: PLANT_GROWTH_ID,
        scope: { kind: 'unit', instanceId },
        persistent: true,
        vars: {},
    });
    ctx.ops.modifyEffectCounter(effectId, GROWTH_KEY, amount, reason);
}
/** Юнит перенаправляет входящие пакеты Роста (сам Подсолнух или его UI-маркер). */
function redirectsGrowth(ctx, instanceId) {
    if (ctx.q.instance(instanceId)?.defId === 'growth_sunflower')
        return true;
    return ctx.q.effectsOn({ kind: 'unit', instanceId })
        .some((effect) => effect.defId === 'growth_sunflower' && effect.vars.growthRedirect === true);
}
/**
 * Дать Растению `amount` Роста одним пакетом. Если получатель — Подсолнух,
 * весь пакет передаётся одному случайному другому союзному Растению на поле
 * (seeded, публичное RandomUnitSelectionResolved). Цепочка через нескольких
 * Подсолнухов конечна: каждый посещённый исключается; когда кандидатов нет,
 * Рост остаётся на текущем держателе.
 */
export function addGrowth(ctx, instanceId, amount, reason) {
    if (!Number.isSafeInteger(amount) || amount <= 0) {
        throw new Error(`addGrowth: amount должен быть положительным safe integer (получено ${String(amount)})`);
    }
    let holderId = instanceId;
    const visited = new Set([instanceId]);
    while (redirectsGrowth(ctx, holderId)) {
        const holder = ctx.q.instance(holderId);
        if (!isLiveOnBoard(holder))
            break;
        const candidates = alliedPlants(ctx, holder.controllerId, { exclude: visited })
            .map((unit) => unit.id);
        if (candidates.length === 0)
            break;
        const selected = ctx.ops.selectRandomUnit(candidates, holderId);
        if (selected === null)
            break;
        visited.add(selected);
        holderId = selected;
    }
    grantGrowthDirect(ctx, holderId, amount, reason);
}
/** Снять ровно `amount` Роста; вызывающий обязан проверить достаточность. */
export function removeGrowth(ctx, instanceId, amount, reason) {
    if (!Number.isSafeInteger(amount) || amount <= 0) {
        throw new Error(`removeGrowth: amount должен быть положительным safe integer (получено ${String(amount)})`);
    }
    const effect = growthEffectOf(ctx, instanceId);
    if (effect === undefined || counterValue(effect.vars[GROWTH_KEY]) < amount) {
        throw new Error(`removeGrowth: на "${instanceId}" нет ${amount} Роста`);
    }
    ctx.ops.modifyEffectCounter(effect.id, GROWTH_KEY, -amount, reason);
}
/** Направленное ЛЕЧЕНИЕ карты (D53+): семантическое UnitHealed с атрибуцией источника. */
export function healUnit(ctx, instanceId, amount, sourceId) {
    ctx.ops.restoreUnitHealth(instanceId, amount, sourceId);
}
export function isDamaged(ctx, instanceId) {
    const stats = ctx.q.stats(instanceId);
    return stats.health < stats.maxHealth;
}
// ---------------------------------------------------------------------------
// Фотосинтез
// ---------------------------------------------------------------------------
/**
 * Фотосинтез: в конце каждого раунда карта получает 1 Рост.
 */
export function photosynthesis() {
    return [
        atRoundEnd({
            source: 'card_effect',
            dependencies: [cardRef(PLANT_GROWTH_ID)],
            run: (ctx) => {
                const self = ctx.self;
                if (!isLiveOnBoard(self))
                    return;
                addGrowth(ctx, self.id, 1, 'photosynthesis');
            },
        }),
    ];
}
/** Взрослое Дерево при любой Гибели создаёт Лист ровно в освободившейся ячейке. */
export function adultTreeDeathLeaf() {
    return onDeath({
        source: 'card_effect',
        dependencies: [cardRef('plant_leaf')],
        run: (ctx, ...args) => {
            const event = args[0];
            const controllerId = event.controllerIdAtDeath ?? event.ownerId;
            ctx.ops.tryCreateToken('plant_leaf', controllerId, event.cellId);
        },
    });
}
// ---------------------------------------------------------------------------
// Крона
// ---------------------------------------------------------------------------
function crownInterceptor() {
    return (ctx, planned) => {
        const self = ctx.self;
        if (self === undefined || self.zone !== 'board' || self.cellId === undefined)
            return undefined;
        if (planned.amount <= 0 || planned.attackGroupId === undefined)
            return undefined;
        if (planned.origin !== 'ordinary_attack' && planned.origin !== 'extra_attack')
            return undefined;
        const activeAttackGroup = self.vars[CROWN_ATTACK_GROUP_KEY];
        const activeLeafId = self.vars[CROWN_PENDING_LEAF_KEY];
        if (self.vars[CROWN_USED_ROUND_KEY] === ctx.round
            && activeAttackGroup === planned.attackGroupId
            && typeof activeLeafId === 'string') {
            const activeLeaf = ctx.q.instance(activeLeafId);
            if (isLiveOnBoard(activeLeaf)) {
                return {
                    amount: 0,
                    transfer: { instanceId: activeLeaf.id, amount: planned.amount },
                };
            }
        }
        if (self.vars[CROWN_USED_ROUND_KEY] === ctx.round)
            return undefined;
        const tableId = tableOf(self.cellId);
        const reservedLeafIds = new Set(ctx.q.unitsOf(self.controllerId).flatMap((unit) => {
            if (unit.id === self.id || unit.vars[CROWN_USED_ROUND_KEY] !== ctx.round)
                return [];
            const leafId = unit.vars[CROWN_PENDING_LEAF_KEY];
            return typeof leafId === 'string' ? [leafId] : [];
        }));
        const leaf = ctx.q.unitsOf(self.controllerId).find((unit) => unit.id !== self.id
            && isLiveOnBoard(unit)
            && tableOf(unit.cellId) === tableId
            && !reservedLeafIds.has(unit.id)
            && ctx.q.cardTraits(unit.id).includes('LEAF'));
        if (leaf === undefined)
            return undefined;
        ctx.ops.setVar(CROWN_USED_ROUND_KEY, ctx.round);
        ctx.ops.setVar(CROWN_PENDING_LEAF_KEY, leaf.id);
        ctx.ops.setVar(CROWN_ATTACK_GROUP_KEY, planned.attackGroupId);
        return {
            amount: 0,
            transfer: { instanceId: leaf.id, amount: planned.amount },
        };
    };
}
/**
 * Крона саженца: первый раз за раунд союзный Лист в той же локации принимает
 * весь боевой урон одной атаки, включая все части серии ударов. Один Лист
 * резервируется только одним саженцем в одновременном бою. Перенос исполняет
 * движок (generic interception transfer); если Лист погиб от переноса,
 * саженец получает 1 Рост. Взрослые формы эту ability не подключают.
 */
export function crown() {
    return [
        beforeDamage(crownInterceptor()),
        afterDamageDealt({
            source: 'card_effect',
            dependencies: [cardRef(PLANT_GROWTH_ID)],
            run: (ctx, ...args) => {
                const event = args[0];
                const self = ctx.self;
                if (self === undefined)
                    return;
                const pendingLeaf = self.vars[CROWN_PENDING_LEAF_KEY];
                if (typeof pendingLeaf !== 'string')
                    return;
                if (event.origin !== 'effect' || event.target.kind !== 'unit' || event.target.instanceId !== pendingLeaf)
                    return;
                ctx.ops.setVar(CROWN_PENDING_LEAF_KEY, null);
                ctx.ops.setVar(CROWN_ATTACK_GROUP_KEY, null);
                if (event.lethal === true && isLiveOnBoard(self)) {
                    addGrowth(ctx, self.id, 1, 'crown');
                }
            },
        }),
    ];
}
// ---------------------------------------------------------------------------
// Созревание
// ---------------------------------------------------------------------------
/**
 * Созревание N: как только на саженце есть N Роста, он тратит ровно N и
 * превращается во взрослую auxiliary-форму. Сохраняются полученный урон
 * (а не абсолютное здоровье), controller, cell, готовность (enteredRound) и
 * unit-scoped effects (включая лишний Рост). Instance vars саженца, включая
 * состояние Кроны, после превращения очищаются.
 */
export function maturation(saplingId, threshold, adult) {
    return afterEvent({
        event: 'EffectCounterChanged',
        run: {
            source: 'card_effect',
            dependencies: [cardRef(adult.id), cardRef(PLANT_GROWTH_ID)],
            run: (ctx, ...args) => {
                const event = args[0];
                const self = ctx.self;
                // EffectCounterChanged, emitted while maturation is resolving, retains
                // the original subscriber. Do not let that queued sapling hook run on
                // the already transformed adult form.
                if (!isLiveOnBoard(self) || self.defId !== saplingId)
                    return;
                if (event.defId !== PLANT_GROWTH_ID || event.key !== GROWTH_KEY)
                    return;
                if (event.scope.kind !== 'unit' || event.scope.instanceId !== self.id)
                    return;
                if (growthOf(ctx, self.id) < threshold)
                    return;
                const damageTaken = self.stats.maxHealth - self.stats.health;
                ctx.ops.transformCard(self.id, adult.id, {
                    attack: adult.attack,
                    health: Math.max(1, adult.health - damageTaken),
                    maxHealth: adult.health,
                }, { enteredRound: self.enteredRound ?? ctx.round });
                // Transform first so the counter event from spending the threshold is
                // subscribed by the adult definition. This lets a large Growth grant
                // immediately feed every complete adult Growth sink.
                removeGrowth(ctx, self.id, threshold, 'maturation');
            },
        },
    });
}
// ---------------------------------------------------------------------------
// Колючки
// ---------------------------------------------------------------------------
export function thornsEffectOf(ctx, cellId) {
    return ctx.q.effectsOn({ kind: 'cell', cellId })
        .find((effect) => effect.defId === PLANT_THORNS_ID);
}
export function thornsValueOf(ctx, cellId) {
    return counterValue(thornsEffectOf(ctx, cellId)?.vars[THORNS_VALUE_KEY]);
}
/**
 * Наложить Колючки value на ячейку: повторные наложения суммируются.
 * `growthRewards` хранит число независимых наград Колючих семян, которые
 * будут выданы Растению напротив при общем срабатывании сложенного эффекта.
 */
export function applyThorns(ctx, cellId, value, ownerId, options = {}) {
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new Error(`applyThorns: value должен быть положительным safe integer (получено ${String(value)})`);
    }
    const growthRewards = options.growthRewards ?? 0;
    if (!Number.isSafeInteger(growthRewards) || growthRewards < 0) {
        throw new Error(`applyThorns: growthRewards должен быть неотрицательным safe integer (получено ${String(growthRewards)})`);
    }
    const existing = thornsEffectOf(ctx, cellId);
    if (existing !== undefined) {
        ctx.ops.modifyEffectCounter(existing.id, THORNS_VALUE_KEY, value, 'thorns');
        if (growthRewards > 0) {
            ctx.ops.modifyEffectCounter(existing.id, THORNS_GROWTH_REWARDS_KEY, growthRewards, 'thorn_seeds');
        }
        return;
    }
    const effectId = ctx.ops.addEffect({
        defId: PLANT_THORNS_ID,
        scope: { kind: 'cell', cellId },
        ...(ctx.self !== undefined ? { sourceId: ctx.self.id } : {}),
        persistent: true,
        vars: { ownerId },
    });
    ctx.ops.modifyEffectCounter(effectId, THORNS_VALUE_KEY, value, 'thorns');
    if (growthRewards > 0) {
        ctx.ops.modifyEffectCounter(effectId, THORNS_GROWTH_REWARDS_KEY, growthRewards, 'thorn_seeds');
    }
}
function firstEmptyCellOnSide(ctx, side, tableId) {
    for (const laneIndex of LANE_INDEXES) {
        const candidate = makeCellId(tableId, laneIndex, side);
        const cell = ctx.q.cell(candidate);
        if (cell !== null && cell.occupantId === null && !ctx.q.isCellReserved(candidate))
            return candidate;
    }
    return undefined;
}
/** Все свободные незарезервированные свои ячейки локации в каноническом порядке. */
export function freeOwnCells(ctx, controllerId, tableId) {
    const side = ctx.q.player(controllerId).side;
    return LANE_INDEXES.flatMap((laneIndex) => {
        const candidate = makeCellId(tableId, laneIndex, side);
        const cell = ctx.q.cell(candidate);
        return cell !== null && cell.occupantId === null && !ctx.q.isCellReserved(candidate)
            ? [candidate]
            : [];
    });
}
/** Первая пустая незарезервированная вражеская ячейка локации в каноническом порядке. */
export function firstEmptyEnemyCell(ctx, controllerId, tableId) {
    return firstEmptyCellOnSide(ctx, ctx.q.player(controllerId).side === 'A' ? 'B' : 'A', tableId);
}
/** Первая пустая незарезервированная своя ячейка локации (для проверки перед созданием). */
export function firstEmptyOwnCell(ctx, controllerId, tableId) {
    return freeOwnCells(ctx, controllerId, tableId)[0];
}
// ---------------------------------------------------------------------------
// Чтение committed-планов (локальные readers по образцу slime-helpers)
// ---------------------------------------------------------------------------
function stepSelection(ctx, stepId) {
    return ctx.plan?.selections?.find((step) => step.stepId === stepId)?.selection ?? [];
}
export function plannedUnitId(ctx, stepId) {
    const candidate = stepSelection(ctx, stepId).find((item) => item.kind === 'unit');
    return candidate?.kind === 'unit' ? candidate.instanceId : undefined;
}
export function plannedCellId(ctx, stepId) {
    const candidate = stepSelection(ctx, stepId).find((item) => item.kind === 'cell');
    return candidate?.kind === 'cell' ? candidate.cellId : undefined;
}
export function plannedTableId(ctx, stepId) {
    const candidate = stepSelection(ctx, stepId).find((item) => item.kind === 'table');
    return candidate?.kind === 'table' ? candidate.tableId : undefined;
}
//# sourceMappingURL=plant-helpers.js.map